// Stable per-profile avatar color from a curated subset of the Lara palette.
// Same candidateId always picks the same color across sessions — no storage.
//
// Plain `id % N` clusters consecutive IDs onto neighbouring palette slots
// (1000000305, 1000000307, 1000000319 all share the same low bits with each
// other) — bad in a list where neighbours benefit most from contrast.
// We run djb2 + a bit-mix step so adjacent IDs scatter across the palette.

const PALETTE = [
  "blue",
  "indigo",
  "purple",
  "pink",
  "cyan",
  "teal",
  "orange",
  "yellow",
] as const;

export type AvatarColor = (typeof PALETTE)[number];

export function avatarColorFor(candidateId: number): AvatarColor {
  return PALETTE[Math.abs(hashCandidateId(candidateId)) % PALETTE.length];
}

/**
 * Group-aware allocation: returns a Map<candidateId, AvatarColor> where every
 * id in the input list gets a *unique* color so long as `ids.length` <= palette
 * size. Each id is placed at its hash position first; if that slot is taken,
 * we walk forward to the next free slot. Stable across renders (we sort by
 * candidateId before assigning), so the same group always produces the same
 * mapping even if the input order differs.
 *
 * Fall-back when there are more ids than colors: collisions can happen for
 * the overflow, but the first `palette.length` ids stay collision-free.
 */
export function assignAvatarColors(ids: number[]): Map<number, AvatarColor> {
  const sorted = [...new Set(ids)].sort((a, b) => a - b);
  const used = new Set<AvatarColor>();
  const result = new Map<number, AvatarColor>();
  for (const id of sorted) {
    let i = Math.abs(hashCandidateId(id)) % PALETTE.length;
    let attempts = 0;
    while (used.has(PALETTE[i]) && attempts < PALETTE.length) {
      i = (i + 1) % PALETTE.length;
      attempts++;
    }
    const color = PALETTE[i];
    used.add(color);
    result.set(id, color);
    if (used.size === PALETTE.length) used.clear();
  }
  return result;
}

function hashCandidateId(candidateId: number): number {
  const s = String(candidateId);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  return h ^ (h >>> 16);
}
